import { useLoad } from '../views/useLoad';
import { useAppServices } from '../views/ViewContext';

/**
 * Readiness d'un objet (ADR-082) : l'état de chaque profil applicable, ses dimensions et ses écarts
 * — nature, exigence, responsable. Présentation seulement : l'état vient de `GetReadiness`, le
 * serveur filtre ce que l'identité peut lire, et aucun état n'est une décision de conformité.
 */
interface Gap {
  id: string;
  requirement_code?: string;
  item_code?: string;
  gap_kind: string;
  severity: string;
  blocking?: boolean;
  recommended_action?: string;
  responsible_party_ref?: { type: string; id: string };
  relation_state?: string;
  status: string;
}

interface Assessment {
  profile_ref: { id: string; version: string };
  state: string;
  score?: number;
  dimensions?: Array<{ dimension?: string; dimension_id?: string; state: string; score?: number }>;
  gaps: Gap[];
}

const STATE_LABEL: Readonly<Record<string, string>> = {
  ready: 'Prêt',
  partially_ready: 'Partiellement prêt',
  not_ready: 'Pas prêt',
  blocked: 'Bloqué',
  conflicting: 'En conflit',
  insufficient: 'Insuffisant pour décider',
  expired: 'Périmé',
  not_assessed: 'Non évalué',
};

const GAP_LABEL: Readonly<Record<string, string>> = {
  missing: 'Manquant',
  source_unknown: 'Source inconnue',
  no_owner: 'Sans propriétaire',
  stale: 'Périmé',
  superseded: 'Remplacé',
  unverified: 'Non vérifié',
  conflicting: 'En conflit',
  unresolved_relationship: 'Relation non résolue',
  source_unavailable: 'Source indisponible',
};

export function ReadinessPanel({ subject }: { subject: { type: string; id: string } }) {
  const services = useAppServices();
  const [loaded] = useLoad<{ assessments: Assessment[] }>(`readiness:${subject.type}:${subject.id}`, async () => (await services.client.query<{ assessments: Assessment[] }>('GetReadiness', { subject_type: subject.type, subject_id: subject.id })).data);
  if (loaded.status !== 'ready' || loaded.value.assessments.length === 0) return null;
  return (
    <section className="ay-card" aria-labelledby="ay-readiness-title">
      <h2 id="ay-readiness-title">Readiness</h2>
      {loaded.value.assessments.map((assessment) => (
        <div key={assessment.profile_ref.id} className="ay-readiness">
          <p className="ay-text">
            <strong>{assessment.profile_ref.id}</strong> :{' '}
            <span className={`ay-badge${assessment.state === 'ready' ? ' ay-badge--success' : assessment.state === 'blocked' || assessment.state === 'conflicting' ? ' ay-badge--danger' : ''}`}>{STATE_LABEL[assessment.state] ?? assessment.state}</span>
            {assessment.score === undefined ? '' : ` — score indicatif ${assessment.score}`}
          </p>
          {(assessment.dimensions ?? []).length > 0 && (
            <ul className="ay-chips" aria-label="Dimensions">
              {(assessment.dimensions ?? []).map((dimension) => (
                <li key={dimension.dimension_id ?? dimension.dimension} className="ay-chip">
                  {dimension.dimension_id ?? dimension.dimension} : {dimension.state}
                </li>
              ))}
            </ul>
          )}
          {assessment.gaps.filter((gap) => gap.status === 'open' || gap.status === 'in_progress').length > 0 && (
            <div className="ay-table-wrap">
              <table className="ay-table">
                <caption>Écarts</caption>
                <thead>
                  <tr>
                    <th scope="col">Nature</th>
                    <th scope="col">Exigence</th>
                    <th scope="col">Action</th>
                    <th scope="col">Responsable</th>
                  </tr>
                </thead>
                <tbody>
                  {assessment.gaps
                    .filter((gap) => gap.status === 'open' || gap.status === 'in_progress')
                    .map((gap) => (
                      <tr key={gap.id}>
                        <td>
                          {GAP_LABEL[gap.gap_kind] ?? gap.gap_kind}
                          {gap.blocking === true ? ' (bloquant)' : ''}
                        </td>
                        <td>
                          {gap.requirement_code}
                          {gap.item_code === undefined ? '' : ` / ${gap.item_code}`}
                        </td>
                        <td>{gap.recommended_action ?? '—'}</td>
                        <td>{gap.responsible_party_ref === undefined ? '—' : `${gap.responsible_party_ref.type} ${gap.responsible_party_ref.id}`}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </section>
  );
}
